import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { deepCopy, isEmptyObject, isStringEmpty, parseEmptyArray } from '@common/helpers';
import {
  AFTER_MASTER_LOADED_SUBSCRIPTION,
  ANA_NAME_CODE,
  PassengerInformationInputModalEditArea,
  PhoneDestinationType,
  RegistrationLabelType,
  SmsChangeWarningMessage,
} from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { MyBookingTabShow } from '@common/helpers/enums/servicing.enum';
import {
  DuringStayInformationData,
  DuringStayInformationParts,
  PREVIOUS_PAGE_IDENTIFIER,
  PassengerInformationGroup,
  PhoneNumberEventType,
  PhoneTypeEventType,
  RepresentativeInformationData,
  RepresentativeInformationParts,
  ServicingFunctionIdType,
  ServicingPageIdType,
  UpgradeNotificationEmailData,
  UpgradeNotificationEmailParts,
} from '@common/interfaces';
import { FrequentFlyerCards } from '@common/interfaces/frequent-flyer-cards';
import { JuniorPilot, PassengerJuniorPilotMap } from '@common/interfaces/mybooking/order-services.interface';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';
import { PassengerInformationInputModalStoreService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal-store.service';
import { PassengerInformationInputModalCommonService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal.common.service';
import { PassengerInformationInputModalService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal.service';
import { SupportModalIdComponent } from '@lib/components/support-class';
import { AlertMessageItem, AlertType, DialogClickType, LoginStatusType, PageType } from '@lib/interfaces';
import { CommonLibService, PageInitService, PageLoadingService } from '@lib/services';
import { of } from 'rxjs';
import { GetBasicReservationInformationResponseData } from 'src/sdk-amcmember';
import { GetOrderResponseData, GetOrderResponseDataAir, OrderSeatItemSeatSelection } from 'src/sdk-servicing';
import { PassengerInformationInputModalFooterComponent } from './passenger-information-input-modal-footer.component';
import { PassengerInformationInputModalHeaderComponent } from './passenger-information-input-modal-header.component';
import { DuringStayInformationComponent } from './sub-components/during-stay-information/during-stay-information.component';
import { PassengerInformationComponent } from './sub-components/passenger-information/passenger-information.component';
import { RepresentativeInformationComponent } from './sub-components/representative-information/representative-information.component';
import { UpgradeNotificationEmailComponent } from './sub-components/upgrade-notification-email/upgrade-notification-email.component';
import { ErrorCodeConstants } from '@conf/app.constants';

/**
 * S04-M010
 */
@Component({
  selector: 'asw-passenger-information-input-modal',
  templateUrl: './passenger-information-input-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PassengerInformationInputModalComponent extends SupportModalIdComponent implements OnInit, AfterViewInit {
  public override subPageId: string = ServicingPageIdType.PASSENGER_INFORMATION_INPUT;
  public override subFunctionId: string = ServicingFunctionIdType.PASSENGER_INFORMATION_REQUUEST;

  /** 予約基本情報取得API応答 */
  public basicReservation?: GetBasicReservationInformationResponseData;

  public loggedInPassenger: PassengerInformationGroup | undefined;

  // 提携では不要の項目だが、引数として必要な場合があるため残す。
  public upgradeNotificationEmailData!: UpgradeNotificationEmailData;
  public duringStayInformationData!: DuringStayInformationData;

  // #region 代表者情報 component
  @ViewChild('representativeInformationComponent')
  public representativeInformationComponent?: RepresentativeInformationComponent;

  public representativeInformationData!: RepresentativeInformationData;

  public representativeInformationParts!: RepresentativeInformationParts;

  private _isOpenRepresentativeInformation: boolean = false;
  set isOpenRepresentativeInformation(value: boolean) {
    this._isOpenRepresentativeInformation = value;

    if (value) {
      this.representativeInformationParts.registrationLabel = RegistrationLabelType.EDITING;
      this._s04StoreSvc.updateEditArea(PassengerInformationInputModalEditArea.REPRESENTATIVE_CONTACT);
      return;
    }

    this.representativeInformationParts.registrationLabel = this.isRepresentativeInformationChanged
      ? RegistrationLabelType.EDITING
      : this.representativeInformationParts.registrationLabel;
  }
  get isOpenRepresentativeInformation(): boolean {
    return this._isOpenRepresentativeInformation;
  }

  private _isRepresentativeInformationChanged: boolean = false;
  set isRepresentativeInformationChanged(value: boolean) {
    this._isRepresentativeInformationChanged = value;
  }
  get isRepresentativeInformationChanged(): boolean {
    return this._isRepresentativeInformationChanged;
  }
  // #endregion 代表者情報component

  // #region 搭乗者情報 component
  @ViewChildren('passengerInformationComponent')
  public passengerInformationComponents?: QueryList<PassengerInformationComponent>;

  public listOfPassengerInfoDataGroup: Array<PassengerInformationGroup> = [];

  onChangeStatePassengerComponent(value: boolean, index: number) {
    this.listOfPassengerInfoDataGroup[index].parts.isOpen = value;

    if (value) {
      this.listOfPassengerInfoDataGroup[index].parts.registrationLabel = RegistrationLabelType.EDITING;
      this._s04StoreSvc.updateEditArea(PassengerInformationInputModalEditArea.TRAVELER_INPUT);
      this._s04StoreSvc.updateTravelerId(this.listOfPassengerInfoDataGroup[index].data.travelerId);
      return;
    }

    if (!this.listIndexOfPassengerInfoChanged.includes(index)) {
      this.listOfPassengerInfoDataGroup[index].parts.registrationLabel = RegistrationLabelType.REGISTERED;
    }
  }

  passengerInfoChange(index: number) {
    if (this.listOfPassengerInfoDataGroup[index].parts.registrationLabel === RegistrationLabelType.REGISTERED) {
      return;
    }

    if (!this.listIndexOfPassengerInfoChanged.includes(index)) {
      this.listIndexOfPassengerInfoChanged.push(index);
    }

    this.listOfPassengerInfoDataGroup[index].parts.registrationLabel = RegistrationLabelType.EDITING;
  }

  private listIndexOfPassengerInfoChanged: Array<number> = [];
  // #endregion 搭乗者情報 component

  // ヘッダーフッター component
  public override headerRef: PassengerInformationInputModalHeaderComponent | null = null;
  public override footerRef: PassengerInformationInputModalFooterComponent | null = null;

  /**
   * サイズ変化を検知する箇所のエレメント
   */
  // @ViewChild('bodyContent') bodyContent?: ElementRef;

  /**
   * ResizeObserver作成、指定したコンテンツのサイズが変更したときに起動する
   */
  // resizeObserver = new ResizeObserver(() => {
  //   this.resizeForce();
  // });

  /**
   * 乗客1の第2緊急連絡先（1件のみ）を取得します。
   * 見つからない場合は、unknown を返します。
   * @returns 乗客1の第2緊急連絡先。見つからない場合は未定義。
   */
  get secondContentOfFirstPassenger() {
    return this.listOfPassengerInfoDataGroup[0].data.contact?.secondContact;
  }

  /**
   * 乗客1の第2緊急連絡先選択が「後で登録する」を選択しているかどうかを取得します。
   * 見つからない場合は、unknown を返します。
   * @returns 乗客1の第2緊急連絡先。見つからない場合は未定義。
   */
  get isSecondContentRegistAfterReservationOfFirstPassenger() {
    return this.listOfPassengerInfoDataGroup[0]?.parts?.isSecondContactRegistAfterReservation;
  }

  /**
   * 乗客 1 の滞在中の住所を取得します。
   * 米国の住所が見つからない場合は、unknown を返します。
   * @returns 乗客 1 の滞在中の住所。見つからない場合は未定義。
   */
  get addressDuringStayOfFirstPassenger() {
    return this.listOfPassengerInfoDataGroup[0].data.immigration.regulatoryDetails.get('US')?.destinationAddresses?.[0];
  }

  get editArea() {
    return PassengerInformationInputModalEditArea;
  }

  get pnr(): GetOrderResponseData | undefined {
    return this._s04StoreSvc.pnr();
  }

  constructor(
    private readonly _change: ChangeDetectorRef,
    private readonly _common: CommonLibService,
    private readonly _router: Router,
    private readonly _pageInitSvc: PageInitService,
    private readonly _pageLoadingSvc: PageLoadingService,
    private readonly _s04StoreSvc: PassengerInformationInputModalStoreService,
    private readonly _s04Svc: PassengerInformationInputModalService,
    private readonly _s04CommonSvc: PassengerInformationInputModalCommonService,
    private readonly _servicingCommonStoreSvc: ServicingCommonStoreService,
    private readonly _myBookingService: MyBookingService
  ) {
    super(_common, _pageInitSvc);
    this.closeWithUrlChange(this._router);
  }

  /** (1) 初期表示処理 */
  override async ngOnInit(): Promise<void> {
    this._pageLoadingSvc.startLoading();

    // (1.2) 画面情報に以下の内容を設定する。
    this.setInitialAswCommon();

    const orderDataFromApi = await this._s04Svc.callGetOrderApi(false);

    // (1.3.2.2) 取得できなかった旨のエラーが返却された場合、共通エラー画面に遷移し、PNRが取得できなかった旨のエラーを表示する。
    if (!orderDataFromApi) {
      this._pageInitSvc.subDynamicInit(this.params);
      this._pageInitSvc.endInit(null);
      this.close();

      return;
    }

    // (1.3.2.1) 取得したPNR情報取得API応答を搭乗者情報入力Storeに保持する。
    this._s04StoreSvc.updateOrderData(orderDataFromApi.data);
    this.params = of({
      amcMember: this._common.amcMemberStoreService.amcMemberData,
      getOrderReply: orderDataFromApi,
    });

    super.ngOnInit();

    // (1.12) 取得したPNR情報取得API応答に基づき、画面を表示する。
    this._pageInitSvc.endInit(null);
    this._change.markForCheck();
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    // if (this.bodyContent) {
    //   this.resizeObserver.observe(this.bodyContent.nativeElement);
    // }

    if (this.footerRef) {
      this.footerRef.applyEvent = async () => {
        await this.onApplyEvent();
      };
    }
  }

  /** @inheritdoc */
  init() {
    this.addSubscription(
      AFTER_MASTER_LOADED_SUBSCRIPTION,
      this._s04CommonSvc.isMasterDataLoaded$.subscribe(async (loaded: boolean) => {
        if (!loaded) {
          return;
        }

        await this.onInitDataSubComponents();
        // 提携では「年齢」「生年月日」が非表示になるため処理を追加（awaitの終了時間が長かった場合を考慮）。
        this._change.markForCheck();
      })
    );
  }

  setInitialAswCommon() {
    this._common.aswCommonStoreService.updateAswCommon({
      subFunctionId: this.subFunctionId,
      subPageId: this.subPageId,
      isEnabledLogin: false,
      isUpgrade: false,
    });
  }

  async onInitDataSubComponents() {
    // (1.5.1.1) 表示用代表者連絡先情報を設定する。
    this.representativeInformationData = this._s04Svc.createRepresentativeInformationData(this.pnr);
    this._s04Svc.initRepresentativeInfoData = deepCopy(this.representativeInformationData);
    this.representativeInformationParts = this._s04Svc.createRepresentativeInformationParts(this.pnr);

    // 提携では不要の項目だが、引数として必要な場合があるため残す。
    this.duringStayInformationData = this._s04Svc.createDuringStayInformationData(this.pnr);
    this.upgradeNotificationEmailData = this._s04Svc.createUpgradeNotificationEmailData(this.pnr);

    // (1.5.1.4) 表示用搭乗者情報を設定する。
    this.listOfPassengerInfoDataGroup = this._s04Svc.createPassengerInformationGroup(this.pnr);
    this._s04Svc.listOfInitPassengerInfoData = this._s04Svc.createPassengerInformationGroup(this.pnr);
    this.loggedInPassenger = this.listOfPassengerInfoDataGroup?.find(
      (passengerInfo) => passengerInfo.data.travelerId === this.pnr?.travelersSummary?.loggedInAmcMemberTravelerId
    );

    this.onHandleScreenMessage();

    // (1.6) 保持している編集エリアの情報を基に、表示エリア判定処理を行う。
    // (1.6.1) 以下の項目に初期値としてfalse(非表示)を設定する。
    // (1.6.1.1) 代表者連絡先情報ブロック表示判定
    this.isOpenRepresentativeInformation = false;
    // (1.6.1.3) 搭乗者情報ブロック表示判定
    this.listOfPassengerInfoDataGroup.forEach((passengerInformation) => {
      passengerInformation.parts.isOpen = false;
    });
    // (1.6.2) 搭乗者情報入力store.editArea＝”representativeContact”の場合、代表者連絡先情報ブロック表示判定にtrue(表示)を設定する。
    this.isOpenRepresentativeInformation =
      this.representativeInformationParts.registrationLabel === RegistrationLabelType.EDITING;
    // (1.6.4) 搭乗者情報入力store.editArea＝”passengerInformation”、かつ遷移元画面.travelerId＝搭乗者情報.idの場合、該当する搭乗者の搭乗者情報ブロック表示判定にtrue(表示)を設定する。
    this.listOfPassengerInfoDataGroup.forEach((passengerInformation) => {
      passengerInformation.parts.isOpen =
        passengerInformation.parts.registrationLabel === RegistrationLabelType.EDITING;
    });

    /**
     * (1.7) ユーザ共通情報.ログインステータス ＝ "REAL_LOGIN"(リアルログイン)の場合、以下処理を行う。
     * 以下処理にて画面.予約基本情報(代表者)及び画面.予約基本情報(搭乗者)の表示用オブジェクトを作成する。
     */
    if (this._common.aswContextStoreService.aswContextData.loginStatus === LoginStatusType.REAL_LOGIN) {
      await this.onHandleBasicReservationInformation();
    }

    // (1.8) PNR情報取得API応答.data.air.tripType=”domestic”の場合、搭乗者基本情報表示可否にtrueを設定する。
    this.listOfPassengerInfoDataGroup.forEach((passengerInfoGroup) => {
      passengerInfoGroup.parts.isDisplayBasicInformation =
        this.pnr?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.Domestic;
    });
    // 提携：国内旅程のグループPNRであっても、代表者連絡先情報は入力不要・表示不要（APIおよびBookと同じ挙動）
    // // (1.8) PNR情報取得API応答.data.air.tripType=”domestic”の場合、かつPNR情報取得API応答.data.orderType.isGroupBooking=trueの場合、代表者連絡先情報表示可否にtrueを設定する。
    // if (
    //   this.pnr?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.Domestic &&
    //   this.pnr?.orderType?.isGroupBooking === true
    // ) {
    //   this.representativeInformationParts.isDisplay = true;
    // }

    const isDisabledUpdateBtn =
      !this.representativeInformationParts.isEmailAddressEditable &&
      !this.representativeInformationParts.isPhoneNumberEditable &&
      this.listOfPassengerInfoDataGroup.every(
        (d) =>
          !Boolean(d?.parts?.isMileageProgramEditable) &&
          !Boolean(d?.parts?.isPassengerEmailEditable) &&
          !Boolean(d?.parts?.isPassengerSmsEditable) &&
          !Boolean(d?.parts?.isSecondContactEditable) &&
          !Boolean(d?.parts?.passportInformationParts?.isPassportEditable) &&
          !Boolean(d?.parts?.immigrationInformationParts?.isHomeAddressCountryEditable) &&
          !Boolean(d?.parts?.immigrationInformationParts?.isChineseCitizenIdentificationEditable) &&
          !Boolean(d?.parts?.immigrationInformationParts?.isVisaEditable) &&
          !Boolean(d?.parts?.immigrationInformationParts?.isGreenCardEditable) &&
          !Boolean(d?.parts?.immigrationInformationParts?.isDestinationAddressEditable) &&
          !Boolean(d?.parts?.immigrationInformationParts?.isRedressNumberEditable) &&
          !Boolean(d?.parts?.immigrationInformationParts?.isKnownTravelerNumberEditable) &&
          !Boolean(d?.parts?.arrivalDepartureNoticeInformationParts?.isArrivalDepartureNoticeInfoEditable) &&
          !Boolean(d?.parts?.supportInformationParts?.isSupportInformationEditable)
      );

    if (this.footerRef && isDisabledUpdateBtn) {
      this.footerRef.handleDisabledBtn();
    }
  }

  onHandleScreenMessage() {
    // 搭乗者連絡先情報複数保持項目有無
    let isMultiPassengerContactInfoRetained: boolean = false;
    // 入国関連情報複数保持項目有無
    let isMultiImmigrationInfoRetained: boolean = false;
    // 発着通知連絡先情報編集可否
    let isArrivalDepartureNotifyEditable: boolean = false;
    // サポート情報複数保持項目有無
    let isMultiSupportInfoRetained: boolean = false;
    // FFP情報編集可否
    let isFFPEditable: boolean = false;

    let isW0488Alerted: boolean = false;
    let isW1050Alerted: boolean = false;

    // 幼児情報表示不可旅客有無
    let isNotDisplayInfatInfo: boolean = false;

    this.listOfPassengerInfoDataGroup.forEach((passengerInfoGroup, index) => {
      // (1.5.2.2.1) 以下のいずれかがfalse(編集不可)の場合、搭乗者連絡先情報複数保持項目有無にtrueを設定する。
      isMultiPassengerContactInfoRetained =
        !passengerInfoGroup.parts.isPassengerEmailEditable ||
        !passengerInfoGroup.parts.isPassengerSmsEditable ||
        !passengerInfoGroup.parts.isSecondContactEditable;

      // (1.5.2.2.2) 以下のいずれかがfalse(編集不可)の場合、入国関連情報複数保持項目有無にtrueを設定する。
      isMultiImmigrationInfoRetained =
        !passengerInfoGroup.parts.passportInformationParts.isPassportEditable ||
        !passengerInfoGroup.parts.immigrationInformationParts.isHomeAddressCountryEditable ||
        !passengerInfoGroup.parts.immigrationInformationParts.isVisaEditable ||
        !passengerInfoGroup.parts.immigrationInformationParts.isGreenCardEditable ||
        !passengerInfoGroup.parts.immigrationInformationParts.isDestinationAddressEditable ||
        !passengerInfoGroup.parts.immigrationInformationParts.isRedressNumberEditable ||
        !passengerInfoGroup.parts.immigrationInformationParts.isKnownTravelerNumberEditable;

      // (1.5.2.2.3) 以下いずれかに該当する場合、サポート情報複数保持項目有無=trueを設定する。
      isMultiSupportInfoRetained = !Boolean(
        passengerInfoGroup.parts?.supportInformationParts?.isSupportInformationEditable
      );

      isArrivalDepartureNotifyEditable =
        ((this.pnr?.contacts ?? ({} as any))?.[passengerInfoGroup.data.travelerId]?.departureArrivalNotifications
          ?.length ?? 0) < 3;

      isFFPEditable = passengerInfoGroup.parts.isMileageProgramEditable;

      // (1.5.6) 以下いずれかの条件に該当する場合、エラーメッセージ＝”W0488”、にてエラー情報を指定し、当画面にてモーダル表示する。
      if (
        !isW0488Alerted &&
        // FFP情報編集可否＝false(編集不可)
        (!isFFPEditable ||
          // 搭乗者連絡先情報複数保持項目有無=true
          isMultiPassengerContactInfoRetained ||
          // 入国関連情報複数保持項目有無=true
          isMultiImmigrationInfoRetained ||
          // 発着通知連絡先情報編集可否＝false(編集不可)
          !isArrivalDepartureNotifyEditable ||
          // サポート情報複数保持項目有無=true
          isMultiSupportInfoRetained ||
          // 入国関連情報.isChineseCitizenIdentificationNumberRegistered＝true(登録あり)、かつ中国公民番号編集可否＝false(編集不可)
          (!passengerInfoGroup.parts.immigrationInformationParts.isChineseCitizenIdentificationEditable &&
            Boolean(this.pnr?.travelers?.[index]?.regulatoryDetails?.isChineseCitizenIdentificationNumberRegistered)))
      ) {
        this._common.alertMessageStoreService.setAlertSubWarningMessage({
          alertType: AlertType.ERROR,
          errorMessageId: 'W1049',
          contentHtml: 'W1049',
          isCloseEnable: true,
        });

        isW0488Alerted = true;
      }

      // (1.5.7) マイレージプログラム名取得可否＝false(取得不可)の場合、エラーメッセージ＝”W1050”にて
      if (!isW1050Alerted && !passengerInfoGroup.parts.isMileageProgramAvailability) {
        this._common.alertMessageStoreService.setAlertSubWarningMessage({
          alertType: AlertType.ERROR,
          contentHtml: 'W1050',
          isCloseEnable: true,
        });

        isW1050Alerted = true;
      }
    });

    // (1.5.2.3) 以下処理にて幼児情報表示不可旅客有無の判定を行う。
    isNotDisplayInfatInfo = Boolean(
      this.pnr?.travelers?.some((passenger) => passenger?.hasInfiniNamelessInfant) ||
        this.pnr?.travelers?.some((passenger) => passenger?.hasNamelessInfant)
    );

    // (1.5.4) 幼児情報表示不可旅客有無=trueの場合、エラーメッセージ＝”E0489”にてエラー情報を指定し、当画面にてモーダル表示する。
    if (isNotDisplayInfatInfo) {
      this._common.alertMessageStoreService.setAlertSubWarningMessage({
        alertType: AlertType.ERROR,
        contentHtml: 'E0489',
        isCloseEnable: true,
      });
    }

    // (1.5.5) 以下いずれかの条件に該当する場合、エラーメッセージ＝”W0776”、にてエラー情報を指定し、当画面にてモーダル表示する。
    if (
      !Boolean(this.representativeInformationParts.isEmailAddressEditable) ||
      !Boolean(this.representativeInformationParts.isPhoneNumberEditable)
    ) {
      this._common.alertMessageStoreService.setAlertSubWarningMessage({
        alertType: AlertType.ERROR,
        errorMessageId: 'W0776',
        contentHtml: 'W0776',
        isCloseEnable: true,
      });
    }
  }

  async onHandleBasicReservationInformation() {
    // (1.7.1) 代表者連絡先情報.emailsの要素数=0、かつ代表者連絡先情報.phonesの要素数=0
    if (
      this.representativeInformationData.emails.length === 0 &&
      this.representativeInformationData.phones.length === 0
    ) {
      // 表示用予約基本情報(代表者)として以下の内容で空のオブジェクトを作成する。
      this.representativeInformationData.basicReservationContactInfo.next({ email: '', phoneNumber: '' });
    }

    if (this.loggedInPassenger) {
      // (1.7.2) 予約基本情報(搭乗者)表示要否として初期値falseを設定する。
      this.loggedInPassenger.parts.isDisplayBasicReservation = false;

      const travelerId = this.loggedInPassenger.data.travelerId ?? '';

      // (1.7.3) 以下条件いずれかに該当する場合、予約基本情報(搭乗者)表示要否にtrueを設定する。
      if (
        // (1.7.3.1) (以降、[ログインユーザ搭乗者ID)].regulatoryDetails.passportsの要素数=0
        (this.pnr?.travelers?.find((item) => item?.id === travelerId)?.regulatoryDetails?.passports ?? []).length ===
          0 ||
        // (1.7.3.2) PNR情報取得API対応応答.data.frequentFlyerCards[ログインユーザ搭乗者ID]の要素数=0
        ((this.pnr?.frequentFlyerCards as FrequentFlyerCards)?.[travelerId]?.fqtv ?? []).length === 0 ||
        // (1.7.3.3) PNR情報取得API対応応答.data.travelerSupports.[ログインユーザ搭乗者ID].isSupportRegistered=false
        ((this.pnr?.travelerSupports ?? ({} as any))?.[travelerId]?.isSupportRegistered ?? false) === false
      ) {
        this.loggedInPassenger.parts.isDisplayBasicReservation = true;
      }
    }
  }

  /**
   * リスニングデータ変更イベント
   * @param passengerIndex
   */
  onDataChange(editArea: PassengerInformationInputModalEditArea, passengerIndex: number = -1) {
    switch (editArea) {
      case this.editArea.REPRESENTATIVE_CONTACT: {
        this.isRepresentativeInformationChanged = true;
        this.passengerInformationComponents?.forEach((passengerComponent) => {
          passengerComponent.representativeData = this.representativeInformationData;
          const smsSendFlag = this._s04CommonSvc.masterData.i18nCountries.find(
            (d) => d.country_2letter_code === this.representativeInformationData?.phones?.[0]?.phoneCountry
          )?.sms_send_flag;
          // (5) 代表者電話番号国 の値変更処理　※代表者連絡先情報ブロック
          if (
            !Boolean(smsSendFlag) &&
            passengerComponent.data?.contact?.passengerSMSDestination === PhoneDestinationType.REPRESENTATIVE
          ) {
            this._common.alertMessageStoreService.setAlertSubInfomationMessage(SmsChangeWarningMessage);
          }
        });
        return;
      }
      default: {
        if (passengerIndex < 0) {
          return;
        }
        /**
         * (26) 住所（ホテル名）の値更新処理　※入国関連情報パーツ
         * (27) 都市名の値更新処理　※入国関連情報パーツ
         * (28) 州名の値更新処理　※入国関連情報パーツ
         * (29) 郵便番号(ZIPコード)の値更新処理　※入国関連情報パーツ
         */
        this.passengerInfoChange(passengerIndex);
      }
    }
  }

  /**
   * (4) 代表者電話番号種別の値変更処理
   * @param value
   */
  onChangePhoneTypeRepresentative(value: PhoneTypeEventType) {
    this._s04Svc.changeRepresentativePhoneType(value, this.listOfPassengerInfoDataGroup);
  }

  /**
   * 代表者電話番号の値変更処理
   * @param value 代表者電話番号の変更情報
   */
  onChangePhoneNumberRepresentative(value: PhoneNumberEventType) {
    this._s04Svc.changeRepresentativePhoneNumber(value, this.listOfPassengerInfoDataGroup);
  }

  openBoxError() {
    if (this.representativeInformationComponent && this.representativeInformationData.isError) {
      this.representativeInformationComponent.isOpen = true;
    }
    this.listOfPassengerInfoDataGroup.forEach((passenger, index) => {
      if (passenger.data.isError) {
        const passengerInformationComponent = this.passengerInformationComponents?.find((_, i) => i === index);
        if (passengerInformationComponent) {
          passengerInformationComponent.isOpen = true;
        }
      }
    });
  }

  /** (44) 更新ボタン押下処理 */
  async onApplyEvent(): Promise<void> {
    this.updateAll();

    const anyPassengerInfoDataError = this.listOfPassengerInfoDataGroup.some(
      (passenger, index) =>
        this.listIndexOfPassengerInfoChanged.includes(index) &&
        (passenger.data.isError ||
          passenger.data.passports.some((passport) => passport.isError) ||
          passenger.data.arrivalAndDepartureNotice.isError ||
          passenger.data.immigration.isError ||
          passenger.data.support.isError)
    );

    // 入力エラーチェック　エラーがある場合処理終了
    if (
      this.representativeInformationData.isError ||
      this.upgradeNotificationEmailData.isError ||
      anyPassengerInfoDataError ||
      this.duringStayInformationData.isError
    ) {
      this.openBoxError();
      return;
    }

    // (44.2) - MSG1047が注意喚起エリア(インフォメーション)に表示されている場合、非表示にする。
    this._common.alertMessageStoreService.removeAlertSubInfomationMessage(SmsChangeWarningMessage.contentId);

    if (!this.validateMileageProgramData()) {
      // 更新ボタン押下処理を終了する。
      return;
    }

    const acceptArrivalDepartureChangedTerm = await this.acceptArrivalDepartureChangedTerm();

    if (!acceptArrivalDepartureChangedTerm) {
      // 更新ボタン押下処理を終了する。
      return;
    }

    if (this.isEmergencySeatAndSupportRegistered()) {
      // 予約詳細(S01-P030)画面に遷移し、更新ボタン押下時処理を終了する。
      this.close();
      return;
    }

    if (!this.validateChildPassengerData()) {
      // 更新ボタン押下処理を終了する。
      return;
    }

    // (44.8) 以下の項目を基に、搭乗者情報更新APIを呼び出す。
    await this.callUpdateTraveler();
  }

  /**
   * (44.3), (44.4)
   * @returns boolean
   */
  validateMileageProgramData(): boolean {
    // (44.3) 登録不可のマイレージプログラムについて、FFPキャリアチェックを行う。
    // (44.3.1) 以下の内容にてASWDB(マスタ)より登録不可なFFPキャリアコード(セット)を取得する。
    const mileageCarrierUnavailableCode = this._common.aswMasterService.getMPropertyByKey(
      'servicing',
      'ffp.unRegistable.carrier'
    );
    // (44.3.2) プロパティ.valueを“|”(パイプ)を区切り文字として分割し、登録不可なFFPキャリアコード(セット)として保持する。
    const mileageCarrierUnavailableCodes = mileageCarrierUnavailableCode?.split('|') ?? [];
    // (44.3.4)
    const isMileageUnavailable = this.listOfPassengerInfoDataGroup.some(
      (passengerInfo) =>
        // 画面.FFP情報.マイレージプログラム名が、登録不可なFFPキャリアコード(セット)に含まれる、
        mileageCarrierUnavailableCodes.includes(
          passengerInfo.data?.listOfMileageProgramInfo?.[0]?.mileageProgramName
        ) &&
        // かつ画面.FFP情報.マイレージプログラム名≠表示用搭乗者情報.FFP情報.マイレージプログラム名の場合、
        passengerInfo.data?.listOfMileageProgramInfo?.[0]?.mileageProgramName !==
          (this.pnr?.frequentFlyerCards as FrequentFlyerCards)?.[passengerInfo.data?.travelerId]?.fqtv?.[0]?.companyCode
    );
    // (44.3.5) FFP情報登録可否＝false(登録不可)である搭乗者情報が存在する場合、エラーメッセージ＝”E0515”にて
    if (isMileageUnavailable) {
      // 継続可能なエラー情報を指定し、当画面でエラーメッセージを表示し、
      this._common.errorsHandlerService.setRetryableError(PageType.SUBPAGE, { errorMsgId: 'E0515' });
      return false;
    }

    // (44.4) 以下の条件を満たす場合、エラーメッセージ＝”E0509”にて
    const checkE0509 =
      // 4.1.	PNR情報取得APIから取得したPNR情報取得API応答.data.air.isContainedNhGroupMarketingSegment＝false
      this.pnr?.air?.isContainedNhGroupMarketingSegment === false &&
      // 4.2.	PNR情報取得APIから取得したPNR情報取得API応答.data.air.isContainedNhGroupOperatingSegment＝false
      this.pnr?.air?.isContainedNhGroupOperatingSegment === false &&
      // 4.3.	画面.FFP情報.マイレージプログラム名≠"nh"(ANAマイレージクラブ)
      this.listOfPassengerInfoDataGroup
        .filter((passenger) => passenger.data.listOfMileageProgramInfo.length === 1)
        .some(
          (passenger) =>
            !isStringEmpty(passenger.data.listOfMileageProgramInfo[0].mileageProgramName) &&
            passenger.data.listOfMileageProgramInfo[0].mileageProgramName !== ANA_NAME_CODE
        );

    if (checkE0509) {
      // 継続可能なエラー情報を指定し、当画面でエラーメッセージを表示し、
      this._common.errorsHandlerService.setRetryableError(PageType.SUBPAGE, { errorMsgId: 'E0509' });
      return false;
    }
    return true;
  }

  /**
   * (44.5)
   * @returns boolean
   */
  async acceptArrivalDepartureChangedTerm(): Promise<boolean> {
    // 画面.発着通知連絡先情報詳細グループのいずれかの項目が空欄ではない、かつ画面.発着通知連絡先情報詳細グループ≠表示用搭乗者情報.発着通知メール情報	である場合、
    const isArrDepInfoChanged = this.listOfPassengerInfoDataGroup.some(
      (passenger, index) =>
        passenger.parts.arrivalDepartureNoticeInformationParts.isArrivalDepartureNoticeInfoEditable &&
        this._s04Svc.checkArrivalDepartureNoticeDataChanged(index, passenger.data.arrivalAndDepartureNotice)
    );

    if (!isArrDepInfoChanged) {
      return true;
    }

    // 以下の確認ダイアログ(G02-005)をそれぞれ表示する。
    const dialogClickResult = await this._s04Svc.openDialogPromise('MSG1046');

    // 5.1.	操作中のユーザの同行者が予約を確認した際、自分以外の搭乗者のメールアドレスが参照できてしまう旨
    // 5.2.	入力された発着通知メールアドレス宛てに発着通知メールアドレス登録完了メールを送信する旨
    if (dialogClickResult === DialogClickType.CONFIRM) {
      return true;
    }

    return false;
  }

  /**
   * (44.6)
   * @returns boolean
   */
  isEmergencySeatAndSupportRegistered(): boolean {
    const flights = this.pnr?.air?.bounds?.flatMap((bound) => bound.flights ?? []) ?? [];
    // PNR情報取得API応答.data.seats.[全セグメントID].[当該搭乗者.id](以降、座席指定情報とする).seatSelection.seatCharacteristicsが”E”(非常口座席)となる
    const emergencySeatExist =
      this.pnr?.travelers?.some((traveler) =>
        flights.some((flight) =>
          (
            this.pnr?.seats?.[flight.id ?? '']?.[traveler.id ?? '']?.seatSelection as OrderSeatItemSeatSelection
          )?.seatCharacteristics?.some((seatCharacteristic) => seatCharacteristic.code === 'E')
        )
      ) ?? false;
    // 座席指定情報が存在する、以下の処理を行う。
    if (emergencySeatExist) {
      // 以下の内容にてASWDB(マスタ)より非常口を指定している場合にリクエストできないサポートを取得する。
      const impossibleEmergencySeatList =
        this._common.aswMasterService.getMPropertyByKey('servicing', 'impossible.emergencySeat.List')?.split('|') ?? [];
      // 以下のいずれかに合致する場合、エラーメッセージID=“E0478”を 注意喚起エリア storeに保存し、予約詳細(S01-P030)画面に遷移し、更新ボタン押下時処理を終了する。
      if (
        this.listOfPassengerInfoDataGroup.some(
          (passenger) =>
            impossibleEmergencySeatList.includes(passenger.data.support.walkLevel) ||
            passenger.data.support.blind === true ||
            passenger.data.support.deaf === true ||
            passenger.data.support.isPregnant === true
        )
      ) {
        this._common.alertMessageStoreService.setAlertWarningMessage({
          contentHtml: 'E0478',
          isCloseEnable: true,
          errorMessageId: 'E0478',
        });

        return true;
      }
    }

    return false;
  }

  /**
   * (44.7)
   * @returns boolean
   */
  validateChildPassengerData(): boolean {
    if (this.pnr?.orderType?.isSoloChildBooking !== true) {
      return true;
    }

    // PNR情報取得API応答.data.orderType.isSoloChildBooking = true、かつ以下の条件に該当する場合、ジュニアパイロットに関するエラーメッセージを表示する
    for (let passengerInfoDataGroup of this.listOfPassengerInfoDataGroup) {
      const ageOfSelectedBirthday = this._s04CommonSvc.getAge(
        passengerInfoDataGroup?.data?.basicInformation.selectYmd ?? new Date(),
        new Date(this.pnr?.air?.bounds?.[0]?.originDepartureDateTime ?? new Date())
      );

      /**
       * (44.7.1) 4歳以下の生年月日を選択した場合、エラーメッセージ＝”E1236”にて継続可能なエラー情報を指定し、
       * 当画面でエラーメッセージを表示し、更新ボタン押下処理を終了する。
       */
      if (ageOfSelectedBirthday <= 4) {
        this._common.errorsHandlerService.setRetryableError(PageType.SUBPAGE, { errorMsgId: 'E1236' });
        return false;
      }

      /**
       * (44.7.2) PNR情報取得API応答.data.services.juniorPilot.<セグメントID>.<搭乗者ID>.serviceStatus  = "notRequested" (ジュニアパイロットの申込状況が未申込)場合、
       * エラーメッセージ＝”E1237”にて継続可能なエラー情報を指定し、当画面でエラーメッセージを表示し、更新ボタン押下処理を終了する。
       */
      const isNotRequested = this.pnr?.air?.bounds?.some((bound) =>
        bound?.flights?.some(
          (flight) =>
            (this.pnr?.services?.juniorPilot as JuniorPilot)?.[flight?.id ?? '']?.[
              passengerInfoDataGroup?.data?.travelerId
            ]?.serviceStatus === PassengerJuniorPilotMap.ServiceStatusEnum.NotRequested
        )
      );
      if (isNotRequested) {
        this._common.errorsHandlerService.setRetryableError(PageType.SUBPAGE, { errorMsgId: 'E1237' });
        return false;
      }

      /**
       * (44.7.3) PNR情報取得API応答.data.services.juniorPilot.<セグメントID>.<搭乗者ID>.serviceStatus  = "unnecessary" (ジュニアパイロットの申込状況が不要の旨申込済み)場合、
       * かつ搭乗者が5歳以上、7歳以下の生年月日を選択した場合、
       * エラーメッセージ＝”E1238”（5歳〜7歳はジュニアパイロット(要ケア)の申込が必要である旨）にて継続可能なエラー情報を指定し、
       * 当画面でエラーメッセージを表示し、更新ボタン押下処理を終了する。
       */
      const isUnnecessary = this.pnr?.air?.bounds?.some((bound) =>
        bound?.flights?.some(
          (flight) =>
            (this.pnr?.services?.juniorPilot as JuniorPilot)?.[flight?.id ?? '']?.[
              passengerInfoDataGroup?.data?.travelerId
            ]?.serviceStatus === PassengerJuniorPilotMap.ServiceStatusEnum.Unnecessary
        )
      );
      if (isUnnecessary && ageOfSelectedBirthday >= 5 && ageOfSelectedBirthday <= 7) {
        this._common.errorsHandlerService.setRetryableError(PageType.SUBPAGE, { errorMsgId: 'E1238' });
        return false;
      }
    }

    return true;
  }

  getMessageWarning(messageCode: string): string {
    switch (messageCode) {
      // 10.1.	APIレスポンス.codeが“WBAZ000242”(予約基本情報更新エラー)に該当する場合、ワーニングメッセージID="E0482"(予約基本情報の更新に失敗した旨)を注意喚起エリアstoreサービスに保存する。
      case ErrorCodeConstants.ERROR_CODES.WBAZ000242:
        return 'E0482';
      // 10.2.	APIレスポンス.codeが“WBAZ000573”(発着通知メールアドレス登録完了メールの送信リクエスト失敗)に該当する場合、ワーニングメッセージID="W1816"(発着通知メールアドレス登録完了メールの送信リクエスト失敗)を注意喚起エリアstoreサービスに 保存する。
      // 提携: 先にメッセージIDを提携で修正
      case ErrorCodeConstants.ERROR_CODES.WBAZ000573:
        return 'W1816'; // 文言調整中 - https://asy-pd.backlog.com/view/ANAUIUXS2_DEV-18032
      // 10.3.	APIレスポンス.codeが“WBAZ000577”(VISA情報登録失敗)に該当する場合、ワーニングメッセージID="W1784"(VISA情報登録失敗)を注意喚起エリアstoreサービスに保存する。
      case ErrorCodeConstants.ERROR_CODES.WBAZ000577:
        return 'W1784'; // 文言調整中 - https://asy-pd.backlog.com/view/ANAUIUXS2_DEV-17287
      // 提携: 10.4. APIレスポンス.codeが“WBAA100012”(NAMEエレメントの生年月日を修正することができず搭乗者情報の更新ができなかった)に該当する場合、ワーニングメッセージID="WB001"(NAMEエレメントの生年月日を修正することができず搭乗者情報の更新ができなかった旨)を注意喚起エリアstoreサービスに保存する。
      case ErrorCodeConstants.ERROR_CODES.WBAA100012:
        return 'WB001';
      default:
        return '';
    }
  }

  /**
   * (44.8) 以下の項目を基に、搭乗者情報更新APIを呼び出す。
   * @returns void
   */
  async callUpdateTraveler(): Promise<void> {
    this._pageLoadingSvc.startLoading();
    const createParam = this._s04Svc.createUpdateDataParam(
      this.listOfPassengerInfoDataGroup,
      this.representativeInformationData,
      this.upgradeNotificationEmailData,
      this.duringStayInformationData
    );
    const requestParam = this._s04Svc.createUpdateTravelersApiRequestParam(createParam);

    // ただし、各情報について変更した項目が存在する場合のみAPIに情報を送信し、変更がない場合は情報を送信しない、当モーダルを閉じ、予約詳細(S01-P030)の[初期表示処理]を呼び出す。
    // ※ 変更されていない項目については送信しない。  （表示されていない、またはラベルで編集不可の項目についても同様とする）
    if (isEmptyObject(requestParam)) {
      this._pageLoadingSvc.endLoading();
      this.close();
      return;
    }

    const updateTravelersResult = await this._s04Svc.callUpdateTravelerApi(requestParam!);

    if (!updateTravelersResult.isSuccess) {
      this._pageLoadingSvc.endLoading();

      // (44.9.3) 予約詳細(S01-P030)画面に遷移し、更新ボタン押下時処理を終了する。
      if (updateTravelersResult.forceProcessEnd) {
        this.close();
        this._myBookingService.runOnInit();
        return;
      }

      return;
    }

    // ※以降の処理は、エラーが発生していない搭乗者情報更新レスポンスが通知されたことを契機に、以下の処理を行う。当処理はstoreを介して行う。
    // (44.10) 搭乗者情報更新APIより以下ワーニングが返却された場合、次画面でワーニングメッセージを表示する。
    // 提携：ワーニングメッセージが存在する場合、すべて画面表示する。
    parseEmptyArray(updateTravelersResult?.warnings)?.forEach((warningCode) => {
      const msgId = this.getMessageWarning(warningCode?.code ?? '');
      if (!isStringEmpty(msgId)) {
        this._common.alertMessageStoreService.setAlertWarningMessage({
          contentHtml: msgId,
          isCloseEnable: true,
          alertType: AlertType.WARNING,
          apiErrorCode: warningCode?.code,
          errorMessageId: msgId,
        });
      }
    });

    // (44.11.1) 以下の内容でPNR情報取得APIを呼び出し、PNR情報を再取得する。
    // REDUNDANT STEP
    // await this._s04Svc.callGetOrderApi(true);

    // (44.11.2) 以下の内容をサービシング共通storeで保持する。

    this._servicingCommonStoreSvc.updateServicingCommon({
      // 登録完了時機能種別
      completedFunction: 'passengerInput',
      // 予約詳細表示タブ
      tabShow: MyBookingTabShow.MyBookingTabShowEnum.Traveler,
      // DxAPIリトリーブ要否フラグ(dxApiRetrieveFlg)
      dxApiRetrieveFlg: true,
    });

    this._pageLoadingSvc.endLoading();
    // (44.11.3) 当モーダルを閉じ、、予約詳細(S01-P030)の[初期表示処理]を呼び出す。
    this.close();
    this._myBookingService.runOnInit();
  }

  /**
   *  サブコンポーネントのonInit
   */
  refreshAll() {
    this.representativeInformationComponent?.refreshForce();
    this.passengerInformationComponents?.forEach((component) => component.refreshForce());

    this._change.detectChanges();
  }

  /**
   *  サブコンポーネントのアップデート
   */
  updateAll() {
    this.representativeInformationComponent?.updateForce(true);
    this.passengerInformationComponents?.forEach((component) => {
      if (this.listIndexOfPassengerInfoChanged.includes(component.passengerIndex)) {
        component.updateForce(true);
      }
    });
  }

  // ログインユーザと同姓同名の搭乗者が複数名存在しない場合のみ、表示する
  isDisplayReflectBtn(): boolean {
    const aswServiceData = this._common.aswServiceStoreService.aswServiceData;
    const namesake = this.listOfPassengerInfoDataGroup?.filter(
      (d) =>
        d.data?.basicInformation?.firstName === aswServiceData?.firstName &&
        d.data?.basicInformation?.lastName === aswServiceData?.lastName
    );
    return namesake.length < 2;
  }

  override reload(): void {
    /* override abstract reload function of base class */
  }

  override destroy(): void {
    this._servicingCommonStoreSvc.updateServicingCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.PASSENGER_INFORMATION_INPUT,
    });

    this.deleteSubscription(AFTER_MASTER_LOADED_SUBSCRIPTION);

    this._s04StoreSvc.updateEditArea('');
  }
}
